import { createMachine } from 'xstate';
import { Model } from 'xstate/lib/model.types';
import {
  BaseContext,
  BaseEvent,
  customElement,
  remoteInput,
  remoteMessage,
  successMessage,
} from '../../../machine';
import React from 'react';

import { checkExistingAccount } from '../ZoryveDirect.services';
import { AddToMobileWalletExisting } from '../ZoryveDirect.components';

const WELCOME = 'WELCOME';
const EMAIL_PHONE_FORM = 'EMAIL_PHONE_FORM';
const EMAIL_PHONE_FORM_ACK = 'EMAIL_PHONE_FORM_ACK';
const GOOD_NEWS = 'GOOD_NEWS';
const CHECK_EXISTING_ACCOUNT = 'CHECK_EXISTING_ACCOUNT';
const CHECK_EXISTING_ACCOUNT_RETRY = 'CHECK_EXISTING_ACCOUNT_RETRY';
const EMAIL_PHONE_FORM_RETRY = 'EMAIL_PHONE_FORM_RETRY';
const EMAIL_PHONE_FORM_RETRY_ACK = 'EMAIL_PHONE_FORM_RETRY_ACK';
const ADD_TO_MOBILE_WALLET_EXISTING = 'ADD_TO_MOBILE_WALLET_EXISTING';
const NOT_FOUND = 'NOT_FOUND';

export const RETRIEVE_FLOW_MESSAGES = {
  WELCOME:
    'You are requesting your ZORYVE Direct Savings Card. First, let\'s make sure that you are in the system.',
  EMAIL_PHONE_FORM:
    'Please provide Patient\'s First Name, Last Name, Date of Birth, and registered Phone or Email.',
  GOOD_NEWS: 'Good news! We found you in our system.',
  EMAIL_PHONE_FORM_RETRY:
    'We couldn\'t find you in our system, call 1-844-979-1288 M–F or retry by entering the details below.',
  ADD_TO_MOBILE_WALLET_EXISTING: <AddToMobileWalletExisting />,
  NOT_FOUND:
    'We couldn\'t find you in our system, call 1-844-979-1288 M–F if you have further questions.',
};

/**
 * ZORYVE Direct Program Flow
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const ZoryveDirectRetrieveFlow = (
  model: Model<BaseContext, BaseEvent, void>,
  initial = WELCOME
) =>
  createMachine<BaseContext, BaseEvent>(
    {
      id: 'ZoryveDirectRetrieveFlow',
      initial: initial ?? WELCOME,
      context: model.initialContext,
      states: {
        // Welcome Message
        [WELCOME]: remoteMessage({
          id: WELCOME,
          message: RETRIEVE_FLOW_MESSAGES.WELCOME,
          nextState: EMAIL_PHONE_FORM,
        }),

        [EMAIL_PHONE_FORM]: remoteInput(model, {
          id: EMAIL_PHONE_FORM,
          message: RETRIEVE_FLOW_MESSAGES.EMAIL_PHONE_FORM,
          component: 'ContextualForm',
          componentInputs: [
            {
              id: 1,
              type: 'date',
              key: 'PATIENT_DOB',
              value: '',
              name: 'Date of Birth (MM/DD/YYYY)',
            },
            {
              id: 2,
              type: 'email',
              key: 'EMAIL',
              value: '',
              name: 'Email',
              validator: 'email',
            },
            {
              id: 3,
              type: 'tel',
              key: 'PHONE',
              value: '',
              name: 'Mobile Number',
              validator: 'phone',
            },
          ],
          nextState: EMAIL_PHONE_FORM_ACK,
        }),

        [EMAIL_PHONE_FORM_ACK]: successMessage({
          id: EMAIL_PHONE_FORM_ACK,
          parent: EMAIL_PHONE_FORM,
          nextState: CHECK_EXISTING_ACCOUNT,
        }),

        [CHECK_EXISTING_ACCOUNT]: {
          id: CHECK_EXISTING_ACCOUNT,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          invoke: {
            id: 'existingAccount',
            src: async () => await checkExistingAccount(),
            onDone: [
              {
                target: GOOD_NEWS,
                cond: 'existingAccount',
              },
              {
                target: EMAIL_PHONE_FORM_RETRY,
              },
            ],
          },
        },

        [GOOD_NEWS]: remoteMessage({
          id: GOOD_NEWS,
          message: RETRIEVE_FLOW_MESSAGES.GOOD_NEWS,
          nextState: ADD_TO_MOBILE_WALLET_EXISTING,
        }),

        [ADD_TO_MOBILE_WALLET_EXISTING]: customElement({
          id: ADD_TO_MOBILE_WALLET_EXISTING,
          jsx: RETRIEVE_FLOW_MESSAGES.ADD_TO_MOBILE_WALLET_EXISTING,
        }),

        [EMAIL_PHONE_FORM_RETRY]: remoteInput(model, {
          id: EMAIL_PHONE_FORM_RETRY,
          message: RETRIEVE_FLOW_MESSAGES.EMAIL_PHONE_FORM_RETRY,
          component: 'ContextualForm',
          componentInputs: [
            {
              id: 1,
              type: 'date',
              key: 'PATIENT_DOB',
              value: '',
              name: 'Date of Birth (MM/DD/YYYY)',
            },
            {
              id: 2,
              type: 'email',
              key: 'EMAIL',
              value: '',
              name: 'Email',
              validator: 'email',
            },
            {
              id: 3,
              type: 'tel',
              key: 'PHONE',
              value: '',
              name: 'Mobile Number',
              validator: 'phone',
            },
          ],
          nextState: EMAIL_PHONE_FORM_RETRY_ACK,
        }),

        [EMAIL_PHONE_FORM_RETRY_ACK]: successMessage({
          id: EMAIL_PHONE_FORM_RETRY_ACK,
          parent: EMAIL_PHONE_FORM_RETRY,
          nextState: CHECK_EXISTING_ACCOUNT_RETRY,
        }),

        [CHECK_EXISTING_ACCOUNT_RETRY]: {
          id: CHECK_EXISTING_ACCOUNT_RETRY,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          invoke: {
            id: 'existingAccount',
            src: async () => await checkExistingAccount(),
            onDone: [
              {
                target: ADD_TO_MOBILE_WALLET_EXISTING,
                cond: 'existingAccount',
              },
              {
                target: NOT_FOUND,
              },
            ],
          },
        },

        [NOT_FOUND]: remoteMessage({
          id: NOT_FOUND,
          message: RETRIEVE_FLOW_MESSAGES.NOT_FOUND,
        }),
      },
    },
    {
      guards: {
        existingAccount: (ctx, event): boolean => {
          console.log('existingAccount', event.data.data.enrollment.CardResponseInfo);
          return event.data.data.enrollment.CardResponseInfo;
        },
      },
    }
  );
